import { useState, useEffect, useCallback } from 'react';

import { useDebounce } from '../../hooks/useDebounce';
import { localStorageService, StorageValidationSchema } from '../../utils/localStorage';
import { NewPromptFormData, UseLocalStorageFormReturn } from './NewPromptPage.types';

const STORAGE_KEY = 'newPromptFormData';

const DEFAULT_FORM_DATA: NewPromptFormData = {
    step: 0,
    promptText: '',
    promptTitle: '',
    promptProblem: '',
    selectedCategories: [],
};

const FORM_DATA_SCHEMA: StorageValidationSchema<NewPromptFormData> = {
    step: {
        type: 'number',
        defaultValue: DEFAULT_FORM_DATA.step,
    },
    promptText: {
        type: 'string',
        defaultValue: DEFAULT_FORM_DATA.promptText,
    },
    promptTitle: {
        type: 'string',
        defaultValue: DEFAULT_FORM_DATA.promptTitle,
    },
    promptProblem: {
        type: 'string',
        defaultValue: DEFAULT_FORM_DATA.promptProblem,
    },
    selectedCategories: {
        type: 'array',
        defaultValue: DEFAULT_FORM_DATA.selectedCategories,
    },
};

export const useLocalStorageForm = (): UseLocalStorageFormReturn => {
    const [formData, setFormData] = useState<NewPromptFormData>(DEFAULT_FORM_DATA);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    // Создаем debounced функцию сохранения с задержкой 300ms
    const debouncedSave = useDebounce((data: NewPromptFormData) => {
        localStorageService.save(STORAGE_KEY, data);
    }, 300);

    // Загрузка данных при монтировании компонента
    useEffect(() => {
        const loadedData = localStorageService.load(STORAGE_KEY, FORM_DATA_SCHEMA);
        setFormData(loadedData);
        setIsLoaded(true);
    }, []);

    // Debounced автосохранение при изменении данных
    useEffect(() => {
        if (isLoaded) {
            debouncedSave(formData);
        }
    }, [formData, isLoaded, debouncedSave]);

    const updateFormData = useCallback((updates: Partial<NewPromptFormData>) => {
        setFormData(prev => ({ ...prev, ...updates }));
    }, []);

    const clearFormData = useCallback(() => {
        setFormData(DEFAULT_FORM_DATA);
        localStorageService.remove(STORAGE_KEY);
    }, []);

    return {
        formData,
        updateFormData,
        clearFormData,
        isLoaded,
    };
};
